import React, { useCallback, useMemo, useRef, useState } from "react";
import { DocumentUploadCard } from "../ui/DocumentUploadCard";
import { labelFileDropOptions } from "./fixtures/IDTypes";
import type { RcFile, UploadFile } from "antd/es/upload/interface";
import {
  isPdfFile,
  getPdfPageCount,
  pdfPageToImageFile,
  pdfToImage,
} from "../../../utils/vault/pdfToImage";
import { toast } from "sonner";
import { X } from "lucide-react";
import { Button } from "../../ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../ui/dialog";
import { DocumentCropperModal } from "../inputs/DocumentCropperModal";

interface Props {
  documentType?: string;
  onFrontChange: (files: UploadFile[]) => void;
  onBackChange: (files: UploadFile[]) => void;
  frontFiles: UploadFile[];
  backFiles: UploadFile[];
}

interface PdfAssignmentState {
  file: File;
  sourceSide: "front" | "back";
  pageCount: number;
  previews: { pageNumber: number; dataUrl: string }[];
  frontPage: number;
  backPage: number;
  isAssigning: boolean;
}

interface PdfCropState {
  frontFile: File;
  backFile: File;
  currentSide: "front" | "back";
  croppedFrontFile: File | null;
}

const PDF_PREVIEW_PAGE_LIMIT = 6;
const TWO_SIDED_DOCUMENT_TYPES = ["ine", "driver_license", "migratory_form"];

const createUploadFile = (file: File, side: "front" | "back"): UploadFile => ({
  uid: `pdf-${side}-${Date.now()}`,
  name: file.name,
  status: "done",
  originFileObj: file as RcFile,
  size: file.size,
  type: file.type,
});

export const IDSwitch: React.FC<Props> = ({
  documentType,
  onFrontChange,
  onBackChange,
  frontFiles,
  backFiles,
}) => {
  const [pdfAssignment, setPdfAssignment] = useState<PdfAssignmentState | null>(
    null,
  );
  const [pdfCropState, setPdfCropState] = useState<PdfCropState | null>(null);
  const frontFileInputRef = useRef<HTMLInputElement | null>(null);
  const backFileInputRef = useRef<HTMLInputElement | null>(null);
  const ignoreNextCropCloseRef = useRef(false);
  const doc: string = labelFileDropOptions[documentType!] || "";

  // Documents that require both front and back
  const requiresBothSides = TWO_SIDED_DOCUMENT_TYPES.includes(
    documentType || "",
  );
  const pageOptions = useMemo(
    () =>
      Array.from(
        { length: pdfAssignment?.pageCount ?? 0 },
        (_, index) => index + 1,
      ),
    [pdfAssignment?.pageCount],
  );

  const handleIdentityPdf = useCallback(
    async (file: File, sourceSide: "front" | "back"): Promise<boolean> => {
      if (!requiresBothSides || !isPdfFile(file)) return false;

      try {
        const pageCount = await getPdfPageCount(file);
        if (pageCount < 2) return false;

        const previewPages = Array.from(
          { length: Math.min(pageCount, PDF_PREVIEW_PAGE_LIMIT) },
          (_, index) => index + 1,
        );
        const previews = await Promise.all(
          previewPages.map(async (pageNumber) => ({
            pageNumber,
            dataUrl: await pdfToImage(file, 0.75, pageNumber),
          })),
        );

        setPdfAssignment({
          file,
          sourceSide,
          pageCount,
          previews,
          frontPage: 1,
          backPage: 2,
          isAssigning: false,
        });
        return true;
      } catch (error) {
        toast.error(
          error instanceof Error
            ? error.message
            : "No se pudo leer el PDF. Intenta con otro archivo.",
        );
        return true;
      }
    },
    [requiresBothSides],
  );

  const closePdfAssignment = useCallback(() => {
    setPdfAssignment(null);
  }, []);

  const chooseAnotherFile = useCallback(() => {
    const sourceSide = pdfAssignment?.sourceSide ?? "front";
    const inputRef =
      sourceSide === "front" ? frontFileInputRef : backFileInputRef;

    setPdfAssignment(null);
    inputRef.current?.click();
  }, [pdfAssignment?.sourceSide]);

  const updatePdfPage = useCallback((side: "front" | "back", page: number) => {
    setPdfAssignment((current) => {
      if (!current) return current;
      return side === "front"
        ? { ...current, frontPage: page }
        : { ...current, backPage: page };
    });
  }, []);

  const assignPdfPages = useCallback(async () => {
    if (!pdfAssignment) return;

    const frontPage = pdfAssignment.frontPage;
    const backPage = pdfAssignment.backPage;

    if (frontPage === backPage) {
      toast.error("Selecciona páginas diferentes para frente y reverso.");
      return;
    }

    setPdfAssignment((current) =>
      current ? { ...current, isAssigning: true } : current,
    );

    try {
      const [frontFile, backFile] = await Promise.all([
        pdfPageToImageFile(pdfAssignment.file, frontPage, 2),
        pdfPageToImageFile(pdfAssignment.file, backPage, 2),
      ]);

      setPdfAssignment(null);
      setPdfCropState({
        frontFile,
        backFile,
        currentSide: "front",
        croppedFrontFile: null,
      });
    } catch (error) {
      toast.error(
        error instanceof Error
          ? error.message
          : "No se pudieron preparar las páginas del PDF.",
      );
      setPdfAssignment((current) =>
        current ? { ...current, isAssigning: false } : current,
      );
    }
  }, [pdfAssignment]);

  const handlePdfCropComplete = useCallback(
    (croppedFile: File) => {
      ignoreNextCropCloseRef.current = true;
      setPdfCropState((current) => {
        if (!current) return current;

        if (current.currentSide === "front") {
          return {
            ...current,
            currentSide: "back",
            croppedFrontFile: croppedFile,
          };
        }

        if (!current.croppedFrontFile) return current;

        onFrontChange([createUploadFile(current.croppedFrontFile, "front")]);
        onBackChange([createUploadFile(croppedFile, "back")]);
        toast.success("PDF asignado a frente y reverso.");
        return null;
      });
    },
    [onBackChange, onFrontChange],
  );

  const cancelPdfCrop = useCallback(() => {
    setPdfCropState(null);
  }, []);

  if (!documentType) return null;

  // Single side (Passport)
  if (!requiresBothSides) {
    return (
      <div className="w-full">
        {/* Label to match the style of FRENTE/REVERSO */}
        <div className="flex items-center justify-between mb-3">
          <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">
            Página principal
          </span>
        </div>
        <DocumentUploadCard
          name="front"
          label={`Página principal del ${doc}`}
          type="both"
          enableCropper={true}
          onFileChange={onFrontChange}
          onRawFileSelect={(file) => handleIdentityPdf(file, "front")}
          value={frontFiles}
        />
      </div>
    );
  }

  // Two sides (INE, Licencia, Forma Migratoria) - stacked vertically
  return (
    <div className="flex flex-col gap-4 w-full">
      <DocumentUploadCard
        name="front"
        label={`Frente de ${doc}`}
        type="both"
        enableCropper={true}
        onFileChange={onFrontChange}
        onRawFileSelect={(file) => handleIdentityPdf(file, "front")}
        fileInputRef={frontFileInputRef}
        value={frontFiles}
        sideIndicator="front"
      />
      <DocumentUploadCard
        name="back"
        label={`Reverso de ${doc}`}
        type="both"
        enableCropper={true}
        onFileChange={onBackChange}
        onRawFileSelect={(file) => handleIdentityPdf(file, "back")}
        fileInputRef={backFileInputRef}
        value={backFiles}
        sideIndicator="back"
      />
      <Dialog
        open={!!pdfAssignment}
        onOpenChange={(open) => {
          if (!open) closePdfAssignment();
        }}
      >
        <DialogContent
          className="top-0 left-0 flex h-[100dvh] w-screen max-w-none translate-x-0 translate-y-0 flex-col gap-0 overflow-hidden rounded-none border-0 p-0 sm:top-[50%] sm:left-[50%] sm:h-auto sm:max-w-[640px] sm:translate-x-[-50%] sm:translate-y-[-50%] sm:rounded-lg sm:border"
          showCloseButton={false}
        >
          <button
            type="button"
            aria-label="Cerrar"
            onClick={closePdfAssignment}
            className="absolute right-4 top-[calc(env(safe-area-inset-top,0px)+1rem)] z-10 flex size-8 items-center justify-center rounded-sm text-slate-500 opacity-70 transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-slate-300 focus:ring-offset-2 sm:top-4"
          >
            <X className="size-4" />
          </button>
          <DialogHeader>
            <div className="px-4 pt-[calc(env(safe-area-inset-top,0px)+1rem)] pb-3 sm:px-6 sm:pt-6 sm:pb-0">
              <DialogTitle className="pr-8 text-lg text-slate-950 sm:pr-0 sm:text-xl">
                Confirmar frente y reverso
              </DialogTitle>
              <DialogDescription className="mt-2 text-sm leading-5 text-slate-500 sm:leading-6">
                Detectamos un PDF con {pdfAssignment?.pageCount ?? 0} páginas.
                Usaremos la página 1 como frente y la página 2 como reverso.
              </DialogDescription>
            </div>
          </DialogHeader>

          {pdfAssignment && (
            <div className="flex-1 space-y-3 overflow-y-auto px-4 pb-4 pt-1 sm:space-y-4 sm:px-6 sm:pb-2">
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                {(["front", "back"] as const).map((side) => {
                  const selectedPage =
                    side === "front"
                      ? pdfAssignment.frontPage
                      : pdfAssignment.backPage;
                  const preview = pdfAssignment.previews.find(
                    (item) => item.pageNumber === selectedPage,
                  );

                  return (
                    <div
                      key={side}
                      className="rounded-lg border border-slate-200 bg-slate-50/70 p-3"
                    >
                      <div className="grid grid-cols-[92px_1fr] gap-3 sm:block">
                        <div className="col-start-2 row-start-1 min-w-0">
                          <div className="flex items-center justify-between gap-3">
                            <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                              {side === "front" ? "Frente" : "Reverso"}
                            </span>
                            <span className="text-xs text-slate-400">
                              Página {selectedPage}
                            </span>
                          </div>
                        </div>

                        <div className="col-start-1 row-span-2 row-start-1 flex aspect-[3/4] items-center justify-center overflow-hidden rounded-md border border-slate-200 bg-white sm:mt-3 sm:aspect-[4/3]">
                          {preview ? (
                            <img
                              src={preview.dataUrl}
                              alt={`Página ${selectedPage}`}
                              className="h-full w-full object-contain"
                            />
                          ) : (
                            <span className="px-2 text-center text-[10px] leading-4 text-gray-400 sm:px-3 sm:text-xs">
                              Vista previa disponible para las primeras{" "}
                              {PDF_PREVIEW_PAGE_LIMIT} páginas
                            </span>
                          )}
                        </div>

                        <div className="col-start-2 row-start-2 min-w-0">
                          <label className="mt-3 block text-xs font-medium text-slate-500 sm:mt-3">
                            Página asignada
                          </label>
                          <select
                            value={selectedPage}
                            onChange={(event) =>
                              updatePdfPage(side, Number(event.target.value))
                            }
                            className="mt-1 h-10 w-full rounded-md border border-slate-200 bg-white px-3 text-sm text-slate-700 outline-none focus:border-slate-400 focus:ring-2 focus:ring-slate-200 sm:h-9"
                            disabled={pdfAssignment.isAssigning}
                          >
                            {pageOptions.map((pageNumber) => (
                              <option key={pageNumber} value={pageNumber}>
                                Página {pageNumber}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              <p className="text-xs leading-5 text-slate-500">
                Las páginas seleccionadas se convertirán a imágenes para validar
                la identificación.
              </p>
            </div>
          )}

          <DialogFooter className="flex-col border-t border-slate-100 bg-white px-4 py-3 pb-[calc(env(safe-area-inset-bottom,0px)+0.75rem)] sm:flex-row sm:gap-2 sm:px-6 sm:py-4">
            <Button
              type="button"
              variant="ghost"
              onClick={chooseAnotherFile}
              disabled={pdfAssignment?.isAssigning}
              className="w-full sm:w-auto"
            >
              Elegir otro archivo
            </Button>
            <Button
              type="button"
              onClick={() => void assignPdfPages()}
              disabled={pdfAssignment?.isAssigning}
              className="w-full sm:w-auto"
            >
              {pdfAssignment?.isAssigning ? "Preparando..." : "Confirmar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <DocumentCropperModal
        key={pdfCropState?.currentSide ?? "pdf-cropper"}
        isOpen={!!pdfCropState}
        onOpenChange={(open) => {
          if (open) return;

          if (ignoreNextCropCloseRef.current) {
            ignoreNextCropCloseRef.current = false;
            return;
          }

          cancelPdfCrop();
        }}
        file={
          pdfCropState?.currentSide === "front"
            ? pdfCropState.frontFile
            : (pdfCropState?.backFile ?? null)
        }
        onCropComplete={handlePdfCropComplete}
        onCancel={cancelPdfCrop}
        cropTitle={
          pdfCropState?.currentSide === "front"
            ? "Ajusta el frente"
            : "Ajusta el reverso"
        }
        cropDescription={
          pdfCropState?.currentSide === "front"
            ? "Recorta la página seleccionada para que solo se vea el frente de la identificación."
            : "Recorta la página seleccionada para que solo se vea el reverso de la identificación."
        }
        previewTitle={
          pdfCropState?.currentSide === "front"
            ? "Confirma el frente"
            : "Confirma el reverso"
        }
        previewDescription={
          pdfCropState?.currentSide === "front"
            ? "Revisa el recorte del frente antes de continuar con el reverso."
            : "Revisa el recorte del reverso antes de finalizar."
        }
      />
    </div>
  );
};
